import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Link, Stack, TextField, Typography,
} from '@mui/material';
import Panel from '../crt/Panel';
import SectionTitle from '../crt/SectionTitle';
import { patreonPostAPI, PatreonSessionHealth } from '../../services/api';

/**
 * Patreon session-cookie status + re-capture.
 *
 * Patreon has no programmatic session refresh (unlike Megaphone's JWT) — the
 * `session_id` cookie is a fixed token that eventually dies (hard expiry /
 * logout / security event). Recovery = paste a fresh value. This panel shows
 * live liveness and lets you re-capture in place; the backend persists to S3
 * and immediately re-probes so you get an instant PASS/FAIL.
 */
const PatreonSessionSetup: React.FC = () => {
  const [health, setHealth] = useState<PatreonSessionHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [cookie, setCookie] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const refresh = useCallback(async (force = false) => {
    setLoading(true);
    try {
      setHealth(await patreonPostAPI.health(force));
    } catch (e: any) {
      setHealth({ ok: false, reachable: false, expired: false, authed_as: null,
        user_id: null, campaign_id: null, detail: null,
        reason: e?.message || String(e) } as PatreonSessionHealth);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const res = await patreonPostAPI.updateSessionCookie(cookie.trim());
      setHealth(res.health);
      if (res.health?.ok) {
        setSaveMsg({ ok: true, text: `Saved — authed as ${res.health.authed_as}.` });
        setCookie('');
      } else {
        setSaveMsg({ ok: false, text: `Saved, but probe failed: ${res.health?.detail || res.health?.reason || 'still not valid'}` });
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      setSaveMsg({ ok: false, text: e?.response?.status === 401 || e?.response?.status === 403
        ? 'Not authorized — set your admin token (the Megaphone MUM Setup tab stores it in localStorage as "adminToken").'
        : detail });
    } finally {
      setSaving(false);
    }
  };

  const statusColor = health?.ok ? 'success' : health?.expired ? 'error' : 'warning';
  const statusText = !health ? '—'
    : health.ok ? `LIVE — authed as ${health.authed_as}`
    : health.expired ? 'DEAD — re-capture needed'
    : 'UNREACHABLE (transient — not a session death)';

  return (
    <Panel label="PATREON :: SESSION COOKIE">
      <SectionTitle>{`> POSTING SESSION LIVENESS + RE-CAPTURE_`}</SectionTitle>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" onClick={() => refresh(true)} disabled={loading}>
            {loading ? <CircularProgress size={16} /> : 'Re-check'}
          </Button>
          <Alert severity={statusColor as any} sx={{ flex: 1, py: 0.5 }}>
            <strong>{statusText}</strong>
            {health?.secret?.captured_at && (
              <span> · captured {health.secret.captured_at} · source {health.secret.source}</span>
            )}
            {health && !health.ok && health.reason && (
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.8em', mt: 0.5 }}>{health.reason}</Box>
            )}
          </Alert>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          One shared session covers <strong>both shows</strong> (Spout Lore + Mall
          Brats = the same Patreon campaign). Patreon sessions can't be
          auto-refreshed, so when this goes <strong>DEAD</strong>, re-capture here:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
          <li>Log into <Link href="https://www.patreon.com" target="_blank" rel="noreferrer">patreon.com</Link> as the creator (Spout Lore).</li>
          <li>Open DevTools → <strong>Application</strong> → Storage → Cookies → <code>https://www.patreon.com</code>.</li>
          <li>Copy the <strong>Value</strong> of the <code>session_id</code> row (NOT the Network → Headers tab — Chrome hides it there).</li>
          <li>Paste below and Save. The server verifies it live.</li>
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            label="session_id"
            size="small" fullWidth
            value={cookie}
            onChange={(e) => setCookie(e.target.value)}
            type="password"
            placeholder="paste the session_id cookie value"
          />
          <Button variant="contained" onClick={save}
            disabled={saving || cookie.trim().length < 10}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
            sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
            {saving ? 'Saving…' : 'Save & verify'}
          </Button>
        </Stack>
        {saveMsg && (
          <Alert severity={saveMsg.ok ? 'success' : 'error'} sx={{ mt: 1.5 }}>{saveMsg.text}</Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Stored encrypted-at-rest in S3 <code>secrets/patreon_session.json</code> (never
          committed). A scheduled probe emails an alert if the session dies; the
          jeeves kiosk shows a <strong>PATREON SESSION</strong> tile.
        </Typography>
      </Box>
    </Panel>
  );
};

export default PatreonSessionSetup;
